import { useEffect, useId, useState, type JSX, type KeyboardEvent } from 'react';
import { STREAM_CATEGORY_MAX } from '../../core/model';
import { searchCategories, type CategorySuggestion } from '../../net/channel-api';
import { categoryRows, categorySlugOf, liveHint, QueryCache, type CategoryRow } from './categorySuggest';
import './components.css';

export const CATEGORY_SEARCH_DEBOUNCE_MS = 200;
const SEARCH_LIMIT = 8;

/** The last 50 searches, shared by every category field. */
export const categorySearchCache = new QueryCache<CategorySuggestion[]>(50);

export interface CategoryFieldProps {
  id?: string;
  label: string;
  value: string;
  /** The control plane to search; '' turns the field into a plain text box. */
  apiUrl: string;
  /** The typed text, on blur or Enter, or on every keystroke when `live`; the chosen name on a pick. */
  onCommit(value: string): void;
  /** Replaces committing the name when a row is picked (the "Use …" row gives the typed text and its slug). */
  onSelect?(choice: { name: string; slug: string }): void;
  /** Enter while no row is highlighted. */
  onEnter?(): void;
  live?: boolean;
  max?: number;
  /** Show the `n/max` counter (default true). */
  counter?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const rowName = (row: CategoryRow): string => (row.kind === 'category' ? row.category.name : row.name);

/**
 * A category text box that suggests categories from `GET /api/categories/search` while typing.
 * Offline, without an API URL, or when the search fails, it is a plain text box.
 */
export function CategoryField({
  id,
  label,
  value,
  apiUrl,
  onCommit,
  onSelect,
  onEnter,
  live,
  max = STREAM_CATEGORY_MAX,
  counter = true,
  placeholder,
  disabled,
  className,
}: CategoryFieldProps): JSX.Element {
  const listId = useId();
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  /** The text being searched; null until the user types. */
  const [query, setQuery] = useState<string | null>(null);
  const [results, setResults] = useState<CategorySuggestion[] | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);

  useEffect(() => {
    const q = query?.trim() ?? '';
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (q === '' || apiUrl.trim() === '' || offline) {
      setResults(null);
      return;
    }
    const key = QueryCache.key(apiUrl, q);
    const cached = categorySearchCache.get(key);
    if (cached) {
      setResults(cached);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchCategories({ apiUrl, q, limit: SEARCH_LIMIT }).then(
        (list) => {
          categorySearchCache.set(key, list);
          if (!cancelled) setResults(list);
        },
        () => {
          if (!cancelled) setResults(null);
        },
      );
    }, CATEGORY_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, apiUrl]);

  const rows = results && query !== null ? categoryRows(draft, results) : [];
  const showList = open && !disabled && rows.length > 0;

  const commit = (): void => {
    if (draft !== value) onCommit(draft);
  };
  const change = (next: string): void => {
    setDraft(next);
    setQuery(next);
    setOpen(true);
    setActive(-1);
    if (live) onCommit(next);
  };
  const choose = (row: CategoryRow): void => {
    const name = rowName(row).slice(0, max);
    const slug = row.kind === 'category' ? row.category.slug : categorySlugOf(name);
    setOpen(false);
    setQuery(null);
    setActive(-1);
    if (onSelect) {
      onSelect({ name, slug });
      return;
    }
    setDraft(name);
    if (name !== value) onCommit(name);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (rows.length === 0) return;
      e.preventDefault();
      if (!showList) {
        setOpen(true);
        setActive(e.key === 'ArrowDown' ? 0 : rows.length - 1);
      } else if (e.key === 'ArrowDown') {
        setActive((a) => (a + 1) % rows.length);
      } else {
        setActive((a) => (a <= 0 ? rows.length - 1 : a - 1));
      }
    } else if (e.key === 'Enter') {
      const row = showList && active >= 0 ? rows[active] : undefined;
      if (row) {
        e.preventDefault();
        choose(row);
        return;
      }
      setOpen(false);
      if (!live) commit();
      if (onEnter) {
        e.preventDefault();
        onEnter();
      }
    } else if (e.key === 'Escape' && showList) {
      // Close the list only; the dialog around the field stays open.
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className={className ? `ui-col ${className}` : 'ui-col'} style={{ gap: 2 }}>
      <div className="category-field">
        <input
          id={id}
          className="ui-input"
          style={{ width: '100%' }}
          role="combobox"
          aria-label={label}
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder}
          maxLength={max}
          disabled={disabled}
          value={draft}
          onChange={(e) => change(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => {
            setOpen(false);
            setActive(-1);
            if (!live) commit();
          }}
        />
        {showList ? (
          <ul id={listId} role="listbox" aria-label={`${label} suggestions`} className="ui-menu category-field-list">
            {rows.map((row, i) => {
              const hint = row.kind === 'category' ? liveHint(row.category.viewerCount) : null;
              return (
                <li
                  key={row.kind === 'category' ? row.category.slug : '\u0000create'}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? 'ui-menu-item category-field-option active' : 'ui-menu-item category-field-option'}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(row)}
                >
                  {row.kind === 'category' ? (
                    <>
                      <span className="category-field-name">{row.category.name}</span>
                      {hint ? <span className="ui-menu-shortcut">{hint}</span> : <span />}
                    </>
                  ) : (
                    <span className="category-field-name">{`Use "${row.name}"`}</span>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      {counter ? (
        <span className="ui-dim stream-info-counter" aria-label={`${label} length`}>
          {draft.length}/{max}
        </span>
      ) : null}
    </div>
  );
}
